import { observer } from 'mobx-react';
import { Input, InputType, InputDropdown, Button } from '../../../../core/components/Index';
import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { useNavigate, useParams } from 'react-router-dom';
import { Steps } from './ApplyModels';
import { Appl } from '../../../../core/Appl';
import CountryList from '../../../../assets/data/Country.json';
import Helper from '../../../../core/services/Helper';
import { useEffect, useRef, useState } from 'react';

const AddressInformation = observer(() => {

  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  vm.navigate = useNavigate();
  let { memberId } = useParams();
  const errorRef = useRef<HTMLDivElement>(null);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.AddressInformation, memberId!);
  }, []);

  useEffect(() => {
    if (vm.addressValidationMessage !== "" && errorRef.current) {
      errorRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [vm.addressValidationMessage, submitAttempt]);

  const handleSubmit = async (submitFunction: () => Promise<boolean>) => {
    setSubmitAttempt(prev => prev + 1);
    await submitFunction();
  };

  return (
    <>
      {
        vm.addressValidationMessage !== "" &&
        <>
          <div ref={errorRef} className="gap-4 p-6 border-[2px] border-[#D82042] bg-[#d8204212] flex rounded-md" >
            <div><svg viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-7 h-7'>
              <path d="M12.5 25.5C15.8152 25.5 18.9946 24.183 21.3388 21.8388C23.683 19.4946 25 16.3152 25 13C25 9.68479 23.683 6.50537 21.3388 4.16117C18.9946 1.81696 15.8152 0.5 12.5 0.5C9.18479 0.5 6.00537 1.81696 3.66117 4.16117C1.31696 6.50537 0 9.68479 0 13C0 16.3152 1.31696 19.4946 3.66117 21.8388C6.00537 24.183 9.18479 25.5 12.5 25.5ZM12.5 6.75C13.1494 6.75 13.6719 7.27246 13.6719 7.92188V13.3906C13.6719 14.04 13.1494 14.5625 12.5 14.5625C11.8506 14.5625 11.3281 14.04 11.3281 13.3906V7.92188C11.3281 7.27246 11.8506 6.75 12.5 6.75ZM10.9375 17.6875C10.9375 17.2731 11.1021 16.8757 11.3951 16.5826C11.6882 16.2896 12.0856 16.125 12.5 16.125C12.9144 16.125 13.3118 16.2896 13.6049 16.5826C13.8979 16.8757 14.0625 17.2731 14.0625 17.6875C14.0625 18.1019 13.8979 18.4993 13.6049 18.7924C13.3118 19.0854 12.9144 19.25 12.5 19.25C12.0856 19.25 11.6882 19.0854 11.3951 18.7924C11.1021 18.4993 10.9375 18.1019 10.9375 17.6875Z" fill="#D82042" />
            </svg>
            </div>
            <div className='-mt-1'>
              <div className='text-[#D82042] text-22px font-semibold mb-1 tracking--1percent'>Invalid Address</div>
              <p className="text-black text-15px tracking--1_5percent  ">
                {Helper.formattedHtml(vm.addressValidationMessage)}
              </p>
            </div>
          </div>
        </>
      }

      <div className="space-y-6 max-w-4xl">
        <h1 id="page-title" className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Your Information</h1>
        <p className="text-black text-15px tracking--1percent leading-6">
          Your address is crucial for receiving important updates and notifications. Please enter your current home address below. If you receive mail at a different location, you may also provide an optional mailing address.
        </p>
        {(vm.homeAddressEditable || vm.mailingAddressEditable) &&
          <p className="text-gray-700 font-medium text-22px" >Address Information</p>
        }
        {!(vm.homeAddressEditable || vm.mailingAddressEditable) &&
          <p className="text-gray-700 font-medium text-22px" >Please review your address information.</p>
        }

        <div className="space-y-4 max-w-2xl">
          {!(vm.homeAddressEditable || vm.mailingAddressEditable) &&
            <div>
              <h4 className="font-semibold">Home Address</h4>
              <div className="p-4">
                {!vm.model.personalInformation.homeAddress ? <p className="text-gray-600 italic">No home address provided.</p> : ''}
                {vm.model.personalInformation.homeAddress && Helper.getFormattedAddress(vm.model.personalInformation.homeAddress, vm.model.personalInformation.homeAddress2, vm.model.personalInformation.homeCity, vm.model.personalInformation.homeState, vm.model.personalInformation.homeZip, vm.model.personalInformation.homeCountry)}
                <div className="flex-1 min-w-[240px]">
                  <button onClick={async () => await vm.setHomeAddressEditable(true)} className="text-primary-500 underline font-semibold mt-2">
                    {vm.model.personalInformation.homeAddress ? 'EDIT' : 'ADD'} ADDRESS
                  </button>
                </div>
              </div>
            </div>
          }
          {vm.homeAddressEditable && <>
            <h4 className="text-18px font-semibold mb-6 text-black">Home Address</h4>
            <div className='md:pl-[19px]'>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-2 mb-3">
                <div>
                  <InputDropdown
                    id="homeCountry"
                    value={vm.model.personalInformation.homeCountry}
                    options={CountryList}
                    optionValue="key"
                    optionLabel="key"
                    label="Country"
                    placeholder='Country'
                    required={false}
                    onChange={(e) => vm.onAddressInfoChange(e, 'homeCountry')}
                    className='!w-full md:max-w-[440px]'
                    //style={{ width: '440px' }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4 mb-6">
                <div>
                  <Input
                    id="homeAddress"
                    value={vm.model.personalInformation?.homeAddress}
                    inputType={InputType.Text}
                    onChange={(e: any) => {
                      vm.onAddressInfoChange(e, 'homeAddress');
                    }}
                    label="Address Line 1"
                    required={false}
                    maxLength={200}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4 mb-6">
                <div>
                  <Input
                    id="homeAddress2"
                    value={vm.model.personalInformation?.homeAddress2}
                    inputType={InputType.Text}
                    onChange={(e: any) => {
                      vm.onAddressInfoChange(e, 'homeAddress2');
                    }}
                    label="Address Line 2"
                    required={false}
                    maxLength={200}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4 mb-6">
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <Input
                      id="homeCity"
                      value={vm.model.personalInformation?.homeCity}
                      inputType={InputType.Text}
                      onChange={(e: any) => {
                        vm.onAddressInfoChange(e, 'homeCity');
                      }}
                      label="City"
                      required={false}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Input
                      id="homeState"
                      value={vm.model.personalInformation?.homeState}
                      inputType={InputType.Text}
                      onChange={(e: any) => {
                        vm.onAddressInfoChange(e, 'homeState');
                      }}
                      label={vm.model.personalInformation.homeCountry === 'United States' ? 'State' : 'State/Province/Region'}
                      required={false}
                      maxLength={50}
                    />
                  </div>
                  <div>
                    <Input
                      id="homeZip"
                      value={vm.model.personalInformation?.homeZip}
                      inputType={InputType.Text}
                      onChange={(e: any) => {
                        vm.onAddressInfoChange(e, 'homeZip');
                      }}
                      label={vm.model.personalInformation.homeCountry === 'United States' ? 'Zip' : 'Zip/Postal Code'}
                      maxLength={vm.model.personalInformation.homeCountry === 'United States' ? 5 : 10}
                      required={false}
                    />
                  </div>
                </div>
              </div></div>
            <div className="flex space-x-4 mt-4">
              <Button
                onClick={async () => {
                  await vm.onHomeAddressAddEditAsync();
                }}
                label={`Save & Continue`}
                rounded
                disabled={vm.model.personalInformation?.homeAddress === ""
                  || vm.model.personalInformation?.homeCity === ""
                  || vm.model.personalInformation?.homeState === ""
                  || vm.model.personalInformation?.homeZip === ""
                }
              />
              {vm.model.personalInformation?.homeAddress &&
                <Button severity="warning" outlined rounded
                  onClick={async () => await vm.setHomeAddressEditable(false)}
                  title='Cancel' />
              }
            </div>
          </>
          }
          {!(vm.homeAddressEditable || vm.mailingAddressEditable) &&
            <div>
              <h4 className="font-semibold">Mailing Address</h4>
              <p className='text-13px text-gray-600'>(If different from your home address.)</p>
              <div className="p-4">

                {!vm.model.personalInformation.mailingAddress ? <p className='text-gray-600 italic'>No mailing address provided.</p> : ''}
                {vm.model.personalInformation.mailingAddress && (
                  <>
                    {Helper.getFormattedAddress(vm.model.personalInformation.mailingAddress, vm.model.personalInformation.mailingAddress2, vm.model.personalInformation.mailingCity, vm.model.personalInformation.mailingState, vm.model.personalInformation.mailingZip, vm.model.personalInformation.mailingCountry)}
                  </>
                )}
                <div className="flex-1 min-w-[240px]">
                  <button onClick={async () => await vm.setMailingAddressEditable(true)} className="text-primary-500 underline font-semibold mt-2">
                    {vm.model.personalInformation.mailingAddress ? 'EDIT' : 'ADD ADDRESS'}
                  </button>
                  {vm.model.personalInformation.mailingAddress &&
                    <>&nbsp;|&nbsp;
                      <button onClick={async () => await vm.removeMailingAddress()} className="text-primary-500 underline font-semibold mt-2">
                        REMOVE
                      </button>
                    </>
                  }
                </div>
              </div>
            </div>
          }
        </div>
        {vm.mailingAddressEditable &&
          <>
            <h4 className="font-semibold">Mailing Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-2 mb-3">
              <div>
                <InputDropdown
                  id="mailingCountry"
                  value={vm.model.personalInformation.mailingCountry}
                  options={CountryList}
                  optionValue="key"
                  optionLabel="key"
                  label="Country"
                  placeholder='Country'
                  required={false}
                  onChange={(e) => vm.onAddressInfoChange(e, 'mailingCountry')}
                  className='w-full !max-w-[440px]'
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-2 mb-3">
              <div>
                <Input
                  id="mailingAddress"
                  value={vm.model.personalInformation.mailingAddress}
                  inputType={InputType.Text}
                  onChange={(e: any) => {
                    vm.onAddressInfoChange(e, 'mailingAddress');
                  }}
                  label="Address Line 1"
                  required={false}
                  maxLength={200}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-2 mb-3">
              <div>
                <Input
                  id="mailingAddress2"
                  value={vm.model.personalInformation.mailingAddress2}
                  inputType={InputType.Text}
                  onChange={(e: any) => {
                    vm.onAddressInfoChange(e, 'mailingAddress2');
                  }}
                  label="Address Line 2"
                  required={false}
                  maxLength={200}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4 mb-6">
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div>
                  <Input
                    id="mailingCity"
                    value={vm.model.personalInformation?.mailingCity}
                    inputType={InputType.Text}
                    onChange={(e: any) => {
                      vm.onAddressInfoChange(e, 'mailingCity');
                    }}
                    label="City"
                    required={false}
                    maxLength={100}
                  />
                </div>
                <div>
                  <Input
                    id="mailingState"
                    value={vm.model.personalInformation?.mailingState}
                    inputType={InputType.Text}
                    onChange={(e: any) => {
                      vm.onAddressInfoChange(e, 'mailingState');
                    }}
                    label={vm.model.personalInformation.mailingCountry === 'United States' ? 'State' : 'State/Province/Region'}
                    required={false}
                    maxLength={50}
                  />
                </div>
                <div>
                  <Input
                    id="mailingZip"
                    value={vm.model.personalInformation?.mailingZip}
                    inputType={InputType.Text}
                    onChange={(e: any) => {
                      vm.onAddressInfoChange(e, 'mailingZip');
                    }}
                    label={vm.model.personalInformation.mailingCountry === 'United States' ? 'Zip' : 'Zip/Postal Code'}
                    maxLength={vm.model.personalInformation.mailingCountry === 'United States' ? 5 : 10}
                    required={false}
                  />
                </div>
              </div>
            </div>
            <div className="flex space-x-4 mt-4">
              <Button
                onClick={async () => {
                  await vm.onMailingAddressAddEditAsync();
                }}
                title='Save & Continue'
                rounded
                disabled={vm.model.personalInformation?.mailingAddress === ""
                  || vm.model.personalInformation?.mailingCity === ""
                  || vm.model.personalInformation?.mailingState === ""
                  || vm.model.personalInformation?.mailingZip === ""
                }
              />
              <Button severity="warning" outlined rounded
                onClick={async () => await vm.setMailingAddressEditable(false)}
                title='Cancel' />
            </div>
          </>
        }

        {/* since the buttons are hidden at layout level, add them here and call the Validation function from state */}
        {!(vm.homeAddressEditable || vm.mailingAddressEditable) && (
          <div className="my-6 flex space-x-4">
            {!vm.navigatedFromReviewScreen &&
              <Button rounded
                onClick={async () => await handleSubmit(() => vm.onAddressInformationStepChangeAsync())}
                label={`Save & Continue`}
              />
            }
            {vm.navigatedFromReviewScreen &&
              <Button title="Return to Review" rounded
                onClick={() => vm.navigateToReviewStepAsync()} />
            }
          </div>
        )}
      </div>
    </>
  );
});

export default AddressInformation;
